/**
 * Edge-case detection and copy (REN-13).
 *
 * The three scenarios below are ones a naive read of the results card would
 * mis-explain, so each gets an explicit plain-English callout above the panel
 * grid whenever it applies:
 *
 *   • `all-cash`         — down payment ≥ 100% of the home price. There's no
 *                          mortgage, so the "mortgage cost" panel headline is
 *                          $0/mo and could look like a bug; the callout names
 *                          what that actually means for the comparison.
 *   • `rent-covers-mortgage` — the user's monthly rent already meets or beats
 *                          the mortgage principal-and-interest payment from
 *                          month one. This is the "trivial break-even"
 *                          scenario named by the ticket — even before the
 *                          renter loses ground to inflation, the buyer is
 *                          banking cash. Whether owning wins on year 1 still
 *                          depends on transaction costs and recurring
 *                          ownership overhead, so the callout is careful to
 *                          say "already covers" rather than "beats overall".
 *   • `never-sell`       — user has explicitly toggled indefinite hold. The
 *                          math skips the hypothetical sell-side friction, so
 *                          the callout tells the reader what changed compared
 *                          to the default framing.
 *
 * All copy lives here so a unit test can assert what the user actually sees.
 * DOM rendering happens in `edgeCases.dom.ts`-adjacent code inside
 * `results.ts`; this module is pure.
 */

import type { ScenarioInputs } from "../defaults";
import { amortizeMortgage } from "../math/mortgage";

const PERCENT_DIVISOR = 100;

export type EdgeCaseKey = "all-cash" | "rent-covers-mortgage" | "never-sell";

export interface EdgeCaseCallout {
  readonly key: EdgeCaseKey;
  readonly title: string;
  readonly body: string;
}

export interface EdgeCaseCurrencyFmt {
  (value: number): string;
}

/**
 * Return every edge-case callout that fires for this scenario, in a stable
 * display order (all-cash first because it changes what "mortgage cost" even
 * means; rent-covers-mortgage second because it explains why the answer is
 * so favorable; never-sell last because it's the user's opt-in modifier).
 *
 * The function is pure and takes only the scenario state — no results object
 * — because every callout is a property of the *inputs*, not the composed
 * output. Keeping it that way lets the callouts render before the first
 * expensive engine pass in tests or SSR contexts.
 */
export function detectEdgeCases(
  state: ScenarioInputs,
  currencyFmt: EdgeCaseCurrencyFmt,
): readonly EdgeCaseCallout[] {
  const callouts: EdgeCaseCallout[] = [];

  const downPayment = state.homePrice * (state.downPaymentPercent / PERCENT_DIVISOR);
  const schedule = amortizeMortgage({
    homePrice: state.homePrice,
    downPayment,
    annualInterestRatePercent: state.annualInterestRatePercent,
    termYears: state.termYears,
  });
  const isAllCash = schedule.loanAmount <= 0;

  if (isAllCash) {
    callouts.push({
      key: "all-cash",
      title: "All-cash purchase — no mortgage",
      body:
        `Your ${currencyFmt(downPayment)} down payment covers the entire ${currencyFmt(state.homePrice)} home price, so there's no mortgage and no principal-and-interest payment to make. ` +
        `The "mortgage cost" panel below shows $0/mo on purpose — ownership's ongoing monthly cost is just property tax, insurance, maintenance, and any HOA. ` +
        `The comparison itself is unchanged: the renter still invests the same ${currencyFmt(downPayment)} at your investment return, so the head-start you'd need to overtake is bigger, not gone.`,
    });
  }

  // Only surface the "rent covers mortgage" callout when there *is* a
  // mortgage — the all-cash callout already covers the P&I = 0 case.
  if (!isAllCash && state.monthlyRent >= schedule.monthlyPayment) {
    callouts.push({
      key: "rent-covers-mortgage",
      title: "Rent already covers the mortgage",
      body:
        `Your rent of ${currencyFmt(state.monthlyRent)}/mo already meets or exceeds the mortgage payment of ${currencyFmt(schedule.monthlyPayment)}/mo from day one, so the buyer is banking cashflow before rent inflation even kicks in. ` +
        `Whether owning wins overall still depends on how quickly property tax, maintenance, and round-trip transaction costs eat into that advantage — the chart above shows exactly where the two paths cross.`,
    });
  }

  if (state.neverSell) {
    callouts.push({
      key: "never-sell",
      title: "Indefinite hold — no selling costs applied",
      body:
        "You've toggled \"never sell,\" so the owner's net worth uses the raw home value minus the outstanding mortgage — no hypothetical selling-agent commission is subtracted. " +
        "This is the honest framing for a forever home, and it lets the buyer overtake the renter earlier than the default \"what if you sold today\" comparison would suggest. " +
        "Everything else — mortgage schedule, recurring costs, opportunity cost — is unchanged.",
    });
  }

  return callouts;
}
